// client/src/utils/followupExport.ts
// CSV builders for the Follow-Up Hub call queue.
//
// Two shapes, because the queue is a one-to-many:
//   - call list  → one row per client group (the thing you actually dial)
//   - contracts  → one row per open contract, group/contact context repeated
//
// Pure string builders: the caller resolves effective status/notes (draft-aware)
// and hands them in, so nothing here needs to know about drafts or locks.

import { extractEmails, type FollowUpRecord } from '../services/followup';

export interface FollowUpExportRow {
  record: FollowUpRecord;
  statusCode: string;
  statusLabel: string;
  notes: string;
  updatedAt: string;
  updatedByName: string;
  updatedByEmail: string;
}

interface Column<T> {
  label: string;
  get: (row: T) => string;
}

// ---------- Cell formatting ----------

// Neutralize spreadsheet formula injection. Notes are free text typed by staff,
// so a note starting with "=" or "+" would otherwise evaluate on open.
function neutralize(value: string): string {
  return /^[=+@\t\r]/.test(value) ? `'${value}` : value;
}

function esc(value: string): string {
  return `"${neutralize(value).replace(/"/g, '""')}"`;
}

// Numbers go out unformatted so Excel can sum them.
function num(value: number | string): string {
  const n = Number(value);
  return isFinite(n) ? String(n) : '';
}

const pad = (n: number) => String(n).padStart(2, '0');

// Sortable and unambiguous in Excel, unlike the localized display format.
function ts(iso: string): string {
  const d = new Date(iso || '');
  if (isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// Contract-delivered is a calendar date, not an instant, so take the date part
// verbatim. Parsing "2026-03-14T00:00:00Z" through Date would shift it to the
// 13th for anyone west of UTC. The source sends unset dates as the epoch.
function dateOnly(value: string): string {
  const s = (value || '').trim();
  if (!s || s.startsWith('1970-01-01')) return '';
  const iso = s.match(/^(\d{4}-\d{2}-\d{2})/);
  if (iso) return iso[1];
  const d = new Date(s);
  if (isNaN(d.getTime())) return s;
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function render<T>(columns: Column<T>[], rows: T[]): string {
  const header = columns.map((c) => esc(c.label)).join(',');
  const body = rows.map((row) => columns.map((c) => esc(c.get(row))).join(',')).join('\n');
  // Excel needs the BOM to read this as UTF-8; client names carry accents.
  return '﻿' + header + '\n' + body + '\n';
}

// ---------- Contact info ----------

function phoneEntries(r: FollowUpRecord): [string, string][] {
  const phones = r.PHONENUMBERS && typeof r.PHONENUMBERS === 'object' ? r.PHONENUMBERS : {};
  return Object.entries(phones)
    .map(([k, v]) => [String(k), String(v ?? '').trim()] as [string, string])
    .filter(([, v]) => v !== '');
}

// Phone types vary per record, so the columns are whatever the exported set
// actually contains — first-seen order, which mirrors the source ordering.
function phoneColumns(rows: FollowUpExportRow[]): Column<FollowUpExportRow>[] {
  const types: string[] = [];
  for (const row of rows) {
    for (const [type] of phoneEntries(row.record)) {
      if (!types.includes(type)) types.push(type);
    }
  }
  return types.map((type) => ({
    label: `Phone — ${type}`,
    get: ({ record }: FollowUpExportRow) => phoneEntries(record).find(([t]) => t === type)?.[1] ?? '',
  }));
}

// ---------- Shared column groups ----------

// Generic over T so the contract-level rows (which extend FollowUpExportRow)
// can reuse these without casting.
function baseColumns<T extends FollowUpExportRow>(): Column<T>[] {
  return [
    { label: 'Group ID', get: ({ record }) => record.GROUP_ID || '' },
    { label: 'Group Name', get: ({ record }) => record.GROUP_NAME || '' },
    { label: 'Office', get: ({ record }) => record.CLIENTOFFICE || '' },
    { label: 'Partner', get: ({ record }) => record.CLIENTPARTNER || '' },
    { label: 'Manager', get: ({ record }) => record.CLIENTMANAGER || '' },
    { label: 'CY WIP', get: ({ record }) => num(record.CY_WIP) },
    { label: 'Contact Name', get: ({ record }) => record.CLIENT_CONTACT_NAME || '' },
    { label: 'Primary Email', get: ({ record }) => extractEmails(record.PARTY_EMAIL || '')[0] || '' },
    { label: 'All Emails', get: ({ record }) => extractEmails(record.PARTY_EMAIL || '').join('; ') },
    { label: 'Primary Phone', get: ({ record }) => phoneEntries(record)[0]?.[1] || '' },
  ];
}

function statusColumns<T extends FollowUpExportRow>(): Column<T>[] {
  return [
    { label: 'Follow-Up Status', get: (row) => row.statusLabel },
    { label: 'Status Code', get: (row) => row.statusCode },
    { label: 'Notes', get: (row) => row.notes },
    { label: 'Last Updated', get: (row) => ts(row.updatedAt) },
    { label: 'Last Updated By', get: (row) => row.updatedByName },
    { label: 'Updated By Email', get: (row) => row.updatedByEmail },
  ];
}

function keyColumn<T extends FollowUpExportRow>(): Column<T> {
  return { label: 'Record Key', get: ({ record }) => String(record.PRIMARY_KEY || '') };
}

function contractsOf(r: FollowUpRecord) {
  return Array.isArray(r.OPEN_CONTRACTS) ? r.OPEN_CONTRACTS : [];
}

function countByStatus(r: FollowUpRecord, status: string): number {
  return contractsOf(r).filter((c) => (c['Contract Status'] || '').toUpperCase() === status).length;
}

function deliveredDates(r: FollowUpRecord): string[] {
  return contractsOf(r)
    .map((c) => dateOnly(c['Contract Delivered'] || ''))
    .filter(Boolean)
    .sort();
}

// ---------- Builders ----------

/** One row per client group — the call list. Contracts are summarized. */
export function buildCallListCsv(rows: FollowUpExportRow[]): string {
  const summary: Column<FollowUpExportRow>[] = [
    { label: 'Open Contracts', get: ({ record }) => String(contractsOf(record).length) },
    { label: 'Contracts Opened', get: ({ record }) => String(countByStatus(record, 'OPENED')) },
    { label: 'Contracts Not Opened', get: ({ record }) => String(countByStatus(record, 'NOT_OPENED')) },
    { label: 'Oldest Delivered', get: ({ record }) => deliveredDates(record)[0] || '' },
    { label: 'Newest Delivered', get: ({ record }) => deliveredDates(record).pop() || '' },
    {
      label: 'Contract Titles',
      get: ({ record }) =>
        contractsOf(record)
          .map((c) => c['Contract Title'] || '')
          .filter(Boolean)
          .join(' | '),
    },
  ];

  return render(
    [...baseColumns(), ...phoneColumns(rows), ...statusColumns(), ...summary, keyColumn()],
    rows,
  );
}

/**
 * One row per open contract, with group + contact + status context repeated.
 * Groups with no open contracts still get a row so nothing drops out silently.
 */
export function buildContractsCsv(rows: FollowUpExportRow[]): string {
  interface ContractRow extends FollowUpExportRow {
    contractTitle: string;
    contractStatus: string;
    contractDelivered: string;
  }

  const flat: ContractRow[] = rows.flatMap((row) => {
    const contracts = contractsOf(row.record);
    if (contracts.length === 0) {
      return [{ ...row, contractTitle: '', contractStatus: '', contractDelivered: '' }];
    }
    return contracts.map((c) => ({
      ...row,
      contractTitle: c['Contract Title'] || '',
      contractStatus: c['Contract Status'] || '',
      contractDelivered: dateOnly(c['Contract Delivered'] || ''),
    }));
  });

  const contractColumns: Column<ContractRow>[] = [
    { label: 'Contract Title', get: (r) => r.contractTitle },
    { label: 'Contract Status', get: (r) => r.contractStatus },
    { label: 'Contract Delivered', get: (r) => r.contractDelivered },
  ];

  return render(
    [
      ...baseColumns<ContractRow>(),
      ...(phoneColumns(rows) as Column<ContractRow>[]),
      ...statusColumns<ContractRow>(),
      ...contractColumns,
      keyColumn<ContractRow>(),
    ],
    flat,
  );
}

// ---------- Download ----------

export function downloadCsv(csv: string, filename: string): void {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function todayStamp(): string {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
